import { useMemo, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import { createTableExportHandlers } from '../utils/tableExportHandlers';

export function useReportTableExports({
    columns,
    records,
    title,
    filename,
    pdfColumns,
    pdfRecords,
    customExcel,
    customPDF,
    customImage,
    requireRecords = true,
}) {
    const exportRef = useRef(null);

    const handlers = useMemo(
        () => createTableExportHandlers({
            columns,
            records: records || [],
            title,
            filename,
            pdfColumns,
            pdfRecords,
            tableElementRef: exportRef,
        }),
        [columns, filename, pdfColumns, pdfRecords, records, title],
    );

    const ensureRecords = useCallback(() => {
        if (requireRecords && !(records || []).length) {
            Swal.fire('Generate the report first, then export.', '', 'info');
            return false;
        }
        return true;
    }, [records, requireRecords]);

    const onExcel = useCallback(() => {
        if (!ensureRecords()) {
            return;
        }
        if (customExcel) {
            customExcel();
            return;
        }
        handlers.onExcel();
    }, [customExcel, ensureRecords, handlers]);

    const onPDF = useCallback(() => {
        if (!ensureRecords()) {
            return;
        }
        if (customPDF) {
            customPDF();
            return;
        }
        handlers.onPDF();
    }, [customPDF, ensureRecords, handlers]);

    const onImage = useCallback(async () => {
        if (!ensureRecords()) {
            return;
        }
        try {
            if (customImage) {
                await customImage();
                return;
            }
            await handlers.onImage();
        } catch (error) {
            Swal.fire(error?.message || 'Image export failed.', '', 'error');
        }
    }, [customImage, ensureRecords, handlers]);

    return {
        exportRef,
        onExcel,
        onPDF,
        onImage,
        exportDisabled: !(records || []).length,
    };
}
